import React, { useState, useEffect, useCallback, useRef } from 'react';
import useUser from '../../hooks/useUser';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  ArrowLeft, Building2, Hash, Mail, Phone, MapPin, FileText,
  CheckCircle, AlertCircle, Clock, XCircle,
  ChevronDown, ChevronRight,
  Pencil, Plus, Trash2, Loader2, X,
  Plug, HeartPulse, Globe, PlugZap, Unplug,
  AlertTriangle,
  TrendingUp, Package, RotateCcw, DollarSign,
  Save, Play, RotateCw, Settings2, Zap,
  Calendar, Send, Smartphone, MessageSquare,
  BarChart3, VolumeX, Bell, Activity,
  Eye, Shield, RefreshCw, Check, Search,
  Star, ShieldAlert, Target, Users, UserPlus, Megaphone,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import AmazonConnectModal from '../../components/Admin/SellerAccounts/AmazonConnectModal';
import FlipkartConnectModal from '../../components/Admin/SellerAccounts/FlipkartConnectModal';
import MeeshoConnectModal from '../../components/Admin/SellerAccounts/MeeshoConnectModal';
import ReconnectModal from '../../components/Admin/SellerAccounts/ReconnectModal';
import MarketplaceModal from '../../components/Admin/SellerAccounts/MarketplaceModal';
import AdsConnectModal from '../../components/Admin/SellerAccounts/AdsConnectModal';
import { useAuth } from '../../context/AuthContext';
import { sellerService } from '../../services/sellerService';
import { sellerAccountService } from '../../services/sellerAccountService';
import apiClient from '../../services/api/client';
import { ENDPOINTS } from '../../services/api/endpoints';

// ─── Configs ─────────────────────────────────────────────────────────────────

const statusConfig = {
  AUTHORIZED: { icon: CheckCircle, label: 'Connected', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  PENDING: { icon: Clock, label: 'Pending', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
  REVOKED: { icon: XCircle, label: 'Disconnected', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
  ERROR: { icon: AlertCircle, label: 'Error', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-500' },
};

const platformConfig = {
  AMAZON: { letter: 'A', bg: 'bg-orange-500', text: 'text-white', badge: 'bg-orange-100 text-orange-700 border-orange-200', label: 'Amazon', gradient: 'from-orange-500 to-amber-500' },
  FLIPKART: { letter: 'F', bg: 'bg-[#232F3E]', text: 'text-white', badge: 'bg-[#FFE8C8] text-[#C45500] border-[#FFD89E]', label: 'Flipkart', gradient: 'from-blue-600 to-blue-500' },
  MEESHO: { letter: 'M', bg: 'bg-[#37475A]', text: 'text-white', badge: 'bg-[#F0F0F0] text-[#37475A] border-[#D5D9D9]', label: 'Meesho', gradient: 'from-purple-600 to-violet-500' },
};

const sellerStatusConfig = {
  ACTIVE: { label: 'Active', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  INACTIVE: { label: 'Inactive', color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400' },
  SUSPENDED: { label: 'Suspended', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Eye },
  { id: 'channels', label: 'Marketplaces', icon: Plug },
  { id: 'notifications', label: 'Notification Config', icon: Bell },
];

// ─── Notification Definitions ───────────────────────────────────────────────

const NOTIFICATION_TYPES = [
  { id: 'DAILY_ORDER_SALES', title: 'Daily Order & Sales Summary', shortTitle: 'Order & Sales', icon: TrendingUp, enabled: true, color: 'emerald', description: 'Automated digest of orders placed, units sold, revenue generated, shipments dispatched, and return requests across all connected channels.' },
  { id: 'PRODUCT_LISTING_STATUS', title: 'Product Listing Status', shortTitle: 'Listing Status', icon: Package, enabled: true, color: 'blue', description: 'Periodic snapshot of product catalog health — active vs inactive listings, out-of-stock SKUs, suppressed ASINs, and listing quality score.' },
  { id: 'FEEDBACK_REVIEW', title: 'Feedback/Review Notification', shortTitle: 'Feedback & Reviews', icon: Star, enabled: true, color: 'amber', description: 'Get notified when new product reviews or seller feedback are posted — track ratings, respond to negative reviews, and monitor review trends.' },
  { id: 'POLICY_WARNING', title: 'Policy Warning Notification', shortTitle: 'Policy Warnings', icon: ShieldAlert, enabled: true, color: 'rose', description: 'Alerts for policy violations, account health warnings, intellectual property complaints, listing suppression notices, and performance metric breaches.' },
  { id: 'SPONSORED_AD_PERFORMANCE', title: 'Sponsored Ad Performance Report', shortTitle: 'Ad Performance', icon: Target, enabled: true, color: 'orange', description: 'Periodic summary of sponsored ad campaigns — impressions, clicks, ACoS, RoAS, spend vs budget, top performing keywords, and bid recommendations.' },
  { id: 'PRODUCT_HIJACK', title: 'Product Hijack Detection', shortTitle: 'Hijack Alerts', icon: Users, enabled: true, color: 'violet', description: 'Detect when unauthorized sellers list on your ASINs — monitor competitor count, Buy Box ownership, pricing undercuts, and listing hijack status in real-time.' },
  { id: 'REVIEW_REQUEST', title: 'Automated Review Requests', shortTitle: 'Review Requests', icon: Star, enabled: true, color: 'indigo', description: 'Automatically send review solicitation emails to buyers for delivered orders within the Amazon eligibility window (5-30 days after delivery).' },
];

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = ['00', '15', '30', '45'];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_CONFIG = {
  isEnabled: false,
  schedule: { frequency: 'daily', hour: 9, minute: '00', period: 'AM', dayOfWeek: 'Monday', dayOfMonth: 1 },
  data: { range: '1', grouping: 'combined', includeEmpty: false },
  channels: {
    whatsapp: { enabled: false, recipients: [] },
    email: { enabled: false, recipients: [] },
    sms: { enabled: false, recipients: [] },
  },
  advanced: { retryAttempts: 3, quietHoursEnabled: false, quietHoursStart: '22:00', quietHoursEnd: '07:00', thresholdEnabled: false, thresholdCondition: 'orders_gt_0', priority: 'normal' },
  lastRun: null, nextRun: null,
};

// ─── Shared Sub-components ──────────────────────────────────────────────────

function ConfirmModal({ isOpen, onClose, onConfirm, title, message, variant = 'warning', loading }) {
  if (!isOpen) return null;
  const colors = variant === 'danger'
    ? { icon: 'bg-red-100 text-red-600', btn: 'bg-red-600 hover:bg-red-700' }
    : { icon: 'bg-amber-100 text-amber-600', btn: 'bg-amber-600 hover:bg-amber-700' };
  const Icon = variant === 'danger' ? Trash2 : AlertTriangle;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="p-6">
          <div className={`w-12 h-12 rounded-xl ${colors.icon} flex items-center justify-center mb-4`}><Icon size={22} /></div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">{title}</h3>
          <p className="text-sm text-slate-500">{message}</p>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${colors.btn} disabled:opacity-50 flex items-center gap-2`}>
            {loading && <Loader2 size={14} className="animate-spin" />}Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function EditSellerModal({ isOpen, onClose, onSave, editData }) {
  const [form, setForm] = useState({ businessName: '', legalName: '', gstin: '', pan: '', contactEmail: '', contactPhone: '', address: '', notes: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (editData) setForm({ businessName: editData.businessName || '', legalName: editData.legalName || '', gstin: editData.gstin || '', pan: editData.pan || '', contactEmail: editData.contactEmail || '', contactPhone: editData.contactPhone || '', address: editData.address || '', notes: editData.notes || '' }); }, [editData, isOpen]);
  if (!isOpen) return null;
  const handleSubmit = async (e) => { e.preventDefault(); if (!form.businessName.trim()) return; setSaving(true); try { await onSave(form); } finally { setSaving(false); } };
  const inputCls = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/30 focus:border-[#f08804] transition-all bg-white placeholder-slate-300";
  const labelCls = "block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1";
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[820px] mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#f08804] to-[#e88b00] flex items-center justify-center shadow-sm">
              <Pencil size={16} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">Edit Seller</h3>
              <p className="text-[11px] text-slate-400">Update business information</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"><X size={16} className="text-slate-400" /></button>
        </div>
        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-3.5">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Business Name <span className="text-red-500">*</span></label><input type="text" value={form.businessName} onChange={(e) => setForm(f => ({ ...f, businessName: e.target.value }))} required className={inputCls} /></div>
              <div><label className={labelCls}>Legal Name</label><input type="text" value={form.legalName} onChange={(e) => setForm(f => ({ ...f, legalName: e.target.value }))} className={inputCls} /></div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div><label className={labelCls}>GSTIN</label><input type="text" value={form.gstin} onChange={(e) => setForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))} maxLength={15} className={`${inputCls} font-mono`} /></div>
              <div><label className={labelCls}>PAN</label><input type="text" value={form.pan} onChange={(e) => setForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))} maxLength={10} className={`${inputCls} font-mono`} /></div>
              <div><label className={labelCls}>Email</label><input type="email" value={form.contactEmail} onChange={(e) => setForm(f => ({ ...f, contactEmail: e.target.value }))} className={inputCls} /></div>
              <div><label className={labelCls}>Phone</label><input type="tel" value={form.contactPhone} onChange={(e) => setForm(f => ({ ...f, contactPhone: e.target.value }))} className={inputCls} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Address</label><input type="text" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} className={inputCls} /></div>
              <div><label className={labelCls}>Notes</label><input type="text" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className={inputCls} /></div>
            </div>
          </div>
          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-3.5 border-t border-slate-100 bg-slate-50/50">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={saving || !form.businessName.trim()} className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-[#0F1111] bg-gradient-to-b from-[#f7ca00] to-[#f0c14b] border border-[#a88734] rounded-lg hover:from-[#f0c14b] hover:to-[#e7ae00] transition-all disabled:opacity-50 shadow-sm">
              {saving && <Loader2 size={14} className="animate-spin" />}{saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

function ConnectPlatformPicker({ isOpen, onClose, onSelect }) {
  if (!isOpen) return null;
  const platforms = [
    { key: 'AMAZON', label: 'Amazon SP-API', desc: 'Connect via Seller Central', bg: 'bg-orange-500', hover: 'hover:border-orange-300 hover:bg-orange-50/50', letter: 'A' },
    { key: 'FLIPKART', label: 'Flipkart', desc: 'Connect via Seller Dashboard', bg: 'bg-[#232F3E]', hover: 'hover:border-[#FFD89E] hover:bg-[#FFF4E6]/50', letter: 'F' },
    { key: 'MEESHO', label: 'Meesho', desc: 'Connect via Supplier Panel', bg: 'bg-[#37475A]', hover: 'hover:border-[#D5D9D9] hover:bg-[#F7F7F7]/50', letter: 'M' },
  ];
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Choose Platform</h3><p className="text-xs text-slate-500 mt-0.5">Select a marketplace to connect</p></div>
        <div className="p-3 space-y-2">
          {platforms.map(p => (
            <button key={p.key} onClick={() => onSelect(p.key)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-slate-200 ${p.hover} transition-all text-left`}>
              <div className={`w-9 h-9 rounded-lg ${p.bg} flex items-center justify-center flex-shrink-0`}><span className="text-white font-bold text-sm">{p.letter}</span></div>
              <div><span className="text-sm font-medium text-slate-800 block">{p.label}</span><span className="text-[11px] text-slate-400">{p.desc}</span></div>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function TagInput({ tags, onAdd, onRemove, placeholder, icon: Icon }) {
  const [inputValue, setInputValue] = useState('');
  const handleKeyDown = (e) => { if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) { e.preventDefault(); onAdd(inputValue.trim()); setInputValue(''); } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) { onRemove(tags.length - 1); } };
  const handlePaste = (e) => { e.preventDefault(); e.clipboardData.getData('text').split(/[,;\n]+/).map(s => s.trim()).filter(Boolean).forEach(item => onAdd(item)); };
  // Flush uncommitted text when user clicks away — prevents losing typed-but-not-entered values on save
  const handleBlur = () => { if (inputValue.trim()) { onAdd(inputValue.trim()); setInputValue(''); } };
  return (
    <div className="border border-slate-200 rounded-lg p-2 focus-within:ring-2 focus-within:ring-[#f08804] focus-within:border-transparent bg-white min-h-[42px]">
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FFF4E6] text-[#C45500] rounded-md text-xs font-medium border border-[#FFE8C8]">
            {Icon && <Icon size={10} />}{tag}
            <button type="button" onClick={() => onRemove(i)} className="ml-0.5 hover:text-red-500 transition-colors"><X size={10} /></button>
          </span>
        ))}
        <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={handleKeyDown} onPaste={handlePaste} onBlur={handleBlur}
          placeholder={tags.length === 0 ? placeholder : ''} className="flex-1 min-w-[140px] text-sm outline-none bg-transparent py-0.5 px-1" />
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, size = 'md', disabled = false }) {
  const sizes = { sm: 'w-8 h-[18px]', md: 'w-10 h-[22px]', lg: 'w-12 h-[26px]' };
  const dotSizes = { sm: 'w-3.5 h-3.5', md: 'w-[18px] h-[18px]', lg: 'w-[22px] h-[22px]' };
  const translate = { sm: 'translate-x-[14px]', md: 'translate-x-[18px]', lg: 'translate-x-[22px]' };
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => !disabled && onChange(!checked)}
      className={`${sizes[size]} rounded-full relative inline-flex items-center transition-colors duration-200 flex-shrink-0 ${checked ? 'bg-[#232F3E]' : 'bg-slate-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
      <span className={`${dotSizes[size]} bg-white rounded-full shadow-sm transition-transform duration-200 ${checked ? translate[size] : 'translate-x-0.5'}`} />
    </button>
  );
}

function SegmentedControl({ options, value, onChange, compact }) {
  return (
    <div className="inline-flex bg-slate-100 rounded-lg p-0.5 border border-slate-200">
      {options.map(opt => (
        <button key={opt.value} type="button" onClick={() => onChange(opt.value)}
          className={`${compact ? 'px-3 py-1.5' : 'px-4 py-1.5'} text-[11px] font-semibold rounded-md transition-all whitespace-nowrap ${value === opt.value ? 'bg-white text-[#C45500] shadow-sm border border-slate-200' : 'text-slate-500 hover:text-slate-700 border border-transparent'}`}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function TimePicker({ hour, minute, period, onChange, disabled }) {
  const selectBase = "px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:border-transparent focus:bg-white transition-colors appearance-none";
  return (
    <div className="inline-flex items-center gap-1.5">
      <select value={hour} onChange={(e) => onChange({ hour: Number(e.target.value), minute, period })} disabled={disabled} className={`${selectBase} w-[56px] ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        {HOURS_12.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="text-slate-300 font-bold text-xs">:</span>
      <select value={minute} onChange={(e) => onChange({ hour, minute: e.target.value, period })} disabled={disabled} className={`${selectBase} w-[56px] ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
      <div className="inline-flex bg-slate-100 rounded-lg p-0.5 border border-slate-200 ml-0.5">
        {['AM', 'PM'].map(p => (
          <button key={p} type="button" disabled={disabled} onClick={() => onChange({ hour, minute, period: p })}
            className={`px-2.5 py-1.5 text-[11px] font-semibold rounded-md transition-all ${period === p ? 'bg-white text-[#C45500] shadow-sm border border-slate-200' : 'text-slate-400 border border-transparent'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

function formatTime12(hour, minute, period) {
  return `${hour}:${minute} ${period}`;
}

// ─── Report Result Sub-components ────────────────────────────────────────────

const metricColors = {
  blue: 'bg-[#FFF4E6] text-[#C45500] border-[#FFE8C8]',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  indigo: 'bg-[#FFF4E6] text-[#C45500] border-[#FFE8C8]',
  rose: 'bg-rose-50 text-rose-700 border-rose-100',
  amber: 'bg-amber-50 text-amber-700 border-amber-100',
  slate: 'bg-slate-50 text-slate-600 border-slate-100',
  violet: 'bg-violet-50 text-violet-700 border-violet-100',
};

function ReportMetric({ label, value, icon: Icon, color = 'indigo' }) {
  const c = metricColors[color] || metricColors.indigo;
  return (
    <div className={`rounded-lg border p-3 ${c}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={11} className="opacity-60" />
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
      </div>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}

function ListingItemsTable({ title, items, color = 'amber' }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, 3);
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
        <span className={`text-[11px] font-bold text-${color}-700`}>{title} ({items.length})</span>
        {items.length > 3 && (
          <button onClick={() => setExpanded(!expanded)} className="text-[10px] font-semibold text-[#C45500] hover:text-[#232F3E]">
            {expanded ? 'Show Less' : `Show All (${items.length})`}
          </button>
        )}
      </div>
      <div className="divide-y divide-slate-50">
        {shown.map((item, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-2 text-[11px]">
            <span className="font-mono text-slate-400 w-24 flex-shrink-0 truncate">{item.sellerSku || item.asin}</span>
            <span className="flex-1 text-slate-700 truncate font-medium">{item.itemName || '—'}</span>
            {item.price != null && <span className="text-slate-500 flex-shrink-0">₹{item.price}</span>}
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${color === 'rose' ? 'bg-rose-50 text-rose-600 border-rose-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
              {item.status || (color === 'rose' ? 'Out of Stock' : 'Inactive')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 1: OVERVIEW
// ═══════════════════════════════════════════════════════════════════════════

function OverviewTab({ seller, onEdit }) {
  const navigate = useNavigate();
  const { isAdminOrManagement } = useAuth();
  const ss = sellerStatusConfig[seller.status] || sellerStatusConfig.ACTIVE;
  const channels = seller.channels || [];
  const connectedCount = channels.filter(c => c.accountStatus === 'AUTHORIZED').length;
  const uniquePlatforms = [...new Set(channels.map(c => c.platform))];

  // Check URL for ?tab=team to pre-select Account Team tab
  const urlParams = new URLSearchParams(window.location.search);
  const [infoTab, setInfoTab] = useState(urlParams.get('tab') === 'team' ? 'team' : 'info');
  const [teamUsers, setTeamUsers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState("");

  const fetchAssignedUsers = useCallback(() => {
    if (!seller?.id) return;
    let cancelled = false;
    async function doFetch() {
      setTeamLoading(true);
      setTeamError("");
      try {
        const res = await apiClient.get(ENDPOINTS.SELLERS.ASSIGNED_USERS(seller.id));
        const arr = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
        if (!cancelled) setTeamUsers(arr);
      } catch (err) {
        if (!cancelled) setTeamError(err?.response?.data?.message || 'Failed to load assigned users');
      } finally {
        if (!cancelled) setTeamLoading(false);
      }
    }
    doFetch();
    return () => { cancelled = true; };
  }, [seller?.id]);

  useEffect(() => { fetchAssignedUsers(); }, [fetchAssignedUsers]);

  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]); // [userId, ...]
  const [selectedRoles, setSelectedRoles] = useState({}); // { userId: roleNumber }
  const [notifyFlags, setNotifyFlags] = useState({}); // { userId: true/false }
  const [assignLoading, setAssignLoading] = useState(false);

  const ROLE_OPTIONS = [
    { value: 5, label: 'Admin' },
    { value: 1, label: 'Branch Manager' },
    { value: 2, label: 'Team Lead' },
    { value: 4, label: 'Account Manager' },
    { value: 3, label: 'BDE' },
    { value: 6, label: 'Seller User' },
  ];

  const { users, roles, fetchRoles, fetchUsers } = useUser();
  const usersLoadedRef = useRef(false);
  const openAssignModal = useCallback(() => {
    setShowAssignUserModal(true);
    setSelectedUsers([]);
    setSelectedRoles({});
    setNotifyFlags({});
    if (!usersLoadedRef.current) {
      usersLoadedRef.current = true;
      fetchRoles();
      fetchUsers();
    }
  }, [fetchRoles, fetchUsers]);

  const handleAssign = async () => {
    if (!selectedUsers.length) return;
    // Validate all selected users have a role
    const missingRole = selectedUsers.filter(uid => !selectedRoles[uid]);
    if (missingRole.length) {
      toast.error('Please select a role for all users');
      return;
    }
    setAssignLoading(true);
    try {
      const payload = selectedUsers.map(userId => {
        const user = users.find(u => u.id === userId);
        return {
          userId,
          role: selectedRoles[userId],
          includeInNotifications: notifyFlags[userId] || false,
          email: user?.email || null,
          phone: user?.phone || null,
        };
      });
      await sellerService.assignUsersToSeller(seller.id, payload);
      toast.success('Users assigned');
      setSelectedUsers([]);
      setSelectedRoles({});
      setNotifyFlags({});
      fetchAssignedUsers();
      setShowAssignUserModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to assign');
    } finally {
      setAssignLoading(false);
    }
  };

  const [removeConfirm, setRemoveConfirm] = useState({ open: false, userId: null, name: '' });
  const [removeLoading, setRemoveLoading] = useState(false);

  const handleRemoveUser = async () => {
    if (!removeConfirm.userId) return;
    setRemoveLoading(true);
    try {
      await sellerService.removeUsersFromSeller(seller.id, [removeConfirm.userId]);
      toast.success(`${removeConfirm.name} removed`);
      fetchAssignedUsers();
      setRemoveConfirm({ open: false, userId: null, name: '' });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove user');
    } finally {
      setRemoveLoading(false);
    }
  };

  const assignedUserIds = new Set(teamUsers.map(u => u.userId));
  const availableUsers = (users || []).filter(u => !assignedUserIds.has(u.id));

  const ROLE_LABELS = { 1: 'Branch Manager', 2: 'Team Lead', 3: 'BDE', 4: 'Account Manager', 5: 'Admin', 6: 'Seller User' };
  const roleLabel = (r) => ROLE_LABELS[r] || (r != null ? `Role ${r}` : null);

  // Notification config count (from seller's channels data)
  const configuredNotifications = seller.notificationTypes?.length || 0;

  return (
    <div className="flex gap-5">
      {/* Left: Main Card */}
      <div className="flex-1 min-w-0 bg-white rounded-xl border border-slate-200/80 overflow-hidden">
        {/* Tab Bar */}
        <div className="flex items-center px-6 border-b border-slate-200">
          <div className="flex items-center gap-0 -mb-px">
            {[
              { id: 'info', label: 'Seller Details', icon: Building2 },
              { id: 'team', label: 'Account Team', icon: Users, count: teamUsers.length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setInfoTab(tab.id)}
                className={`relative flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-colors ${
                  infoTab === tab.id
                    ? 'text-[#C45500]'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <tab.icon size={15} className={infoTab === tab.id ? 'text-[#f08804]' : 'text-slate-300'} />
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full min-w-[22px] text-center ${
                    infoTab === tab.id ? 'bg-[#FFE8C8] text-[#C45500]' : 'bg-slate-100 text-slate-500'
                  }`}>{tab.count}</span>
                )}
                {infoTab === tab.id && (
                  <span className="absolute bottom-0 left-2 right-2 h-[2.5px] bg-[#232F3E] rounded-full" />
                )}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          {infoTab === 'info' ? (
            <button onClick={onEdit} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[12px] font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
              <Pencil size={11} /> Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={fetchAssignedUsers} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" title="Refresh">
                <RefreshCw size={13} className={teamLoading ? 'animate-spin' : ''} />
              </button>
              {isAdminOrManagement() && (
                <button onClick={openAssignModal} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[12px] font-semibold text-[#C45500] bg-[#FFF4E6] hover:bg-[#FFE8C8] rounded-lg transition-colors border border-[#FFE8C8]">
                  <Plus size={12} /> Assign Member
                </button>
              )}
            </div>
          )}
        </div>

        {/* Details Tab */}
        {infoTab === 'info' && (
          <div className="grid grid-cols-1 sm:grid-cols-2">
            <div className="sm:border-r border-slate-100">
              <div className="px-6 py-4 border-b border-slate-50">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Business Name</p>
                <p className="text-sm font-semibold text-slate-900">{seller.businessName || <span className="text-slate-300 font-normal italic">--</span>}</p>
              </div>
              <div className="px-6 py-4 border-b border-slate-50">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Legal Entity</p>
                <p className="text-sm text-slate-700">{seller.legalName || <span className="text-slate-300 italic">--</span>}</p>
              </div>
              <div className="px-6 py-4 border-b border-slate-50">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">GSTIN</p>
                <p className={`text-sm ${seller.gstin ? 'text-slate-700 font-mono tracking-wider' : 'text-slate-300 italic'}`}>{seller.gstin || '--'}</p>
              </div>
              <div className="px-6 py-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">PAN</p>
                <p className={`text-sm ${seller.pan ? 'text-slate-700 font-mono tracking-wider' : 'text-slate-300 italic'}`}>{seller.pan || '--'}</p>
              </div>
            </div>
            <div>
              <div className="px-6 py-4 border-b border-slate-50">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Email</p>
                <p className="text-sm text-slate-700">{seller.contactEmail || <span className="text-slate-300 italic">--</span>}</p>
              </div>
              <div className="px-6 py-4 border-b border-slate-50">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Phone</p>
                <p className="text-sm text-slate-700">{seller.contactPhone || <span className="text-slate-300 italic">--</span>}</p>
              </div>
              <div className="px-6 py-4 border-b border-slate-50">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Address</p>
                <p className="text-sm text-slate-700 leading-relaxed">{seller.address || <span className="text-slate-300 italic">--</span>}</p>
              </div>
              <div className="px-6 py-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Notes</p>
                <p className="text-sm text-slate-500 leading-relaxed">{seller.notes || <span className="text-slate-300 italic">--</span>}</p>
              </div>
            </div>
          </div>
        )}

        {/* Team Tab */}
        {infoTab === 'team' && (
          <div>
            {teamLoading ? (
              <div className="flex items-center justify-center py-16 gap-2">
                <Loader2 size={16} className="animate-spin text-slate-400" />
                <span className="text-sm text-slate-400">Loading...</span>
              </div>
            ) : teamError ? (
              <div className="text-center text-red-500 text-sm py-16">{teamError}</div>
            ) : teamUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                  <Users size={20} className="text-slate-300" />
                </div>
                <p className="text-sm font-semibold text-slate-600">No team members assigned</p>
                <p className="text-xs text-slate-400 mt-1">Add members to manage this seller account</p>
                {isAdminOrManagement() && (
                  <button onClick={openAssignModal} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-[#232F3E] hover:bg-[#37475A] rounded-lg transition-colors shadow-sm">
                    <Plus size={12} /> Assign Member
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest py-2.5 px-6">Name</th>
                      <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest py-2.5 px-4">Role</th>
                      <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest py-2.5 px-4">Email</th>
                      <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest py-2.5 px-4">Phone</th>
                      <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest py-2.5 px-4">Status</th>
                      <th className="py-2.5 px-4 w-[90px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamUsers.map((user, idx) => {
                      const initials = (user.fullName || '??').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
                      const colors = ['from-[#f08804] to-blue-600', 'from-emerald-500 to-teal-600', 'from-amber-500 to-orange-600', 'from-violet-500 to-purple-600', 'from-rose-500 to-pink-600'];
                      const grad = colors[idx % colors.length];
                      return (
                        <tr key={`${user.userId}-${idx}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                          <td className="py-3 px-6">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${grad} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                                <span className="text-[10px] font-bold text-white">{initials}</span>
                              </div>
                              <span className="text-[13px] font-semibold text-slate-800 truncate">{user.fullName}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {roleLabel(user.role) ? (
                              <span className="text-[11px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md">{roleLabel(user.role)}</span>
                            ) : (
                              <span className="text-[11px] text-slate-300">--</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-[12px] text-slate-600">{user.email || '--'}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-[12px] text-slate-600">{user.phone || '--'}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${user.active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                              {user.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {isAdminOrManagement() && (
                              <button
                                onClick={() => setRemoveConfirm({ open: true, userId: user.userId, name: user.fullName })}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold text-red-500 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-all"
                              >
                                <Trash2 size={11} /> Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right Sidebar */}
      <div className="w-64 flex-shrink-0 space-y-4">
        {/* Status */}
        <div className="bg-white rounded-xl border border-slate-200/80 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</span>
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${ss.bg} ${ss.color} border ${ss.border}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
              {ss.label}
            </span>
          </div>
          {seller.createdDateTime && (
            <p className="text-[11px] text-slate-400 mt-2">Since {new Date(seller.createdDateTime).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
          )}
        </div>

        {/* Marketplaces */}
        <div className="bg-white rounded-xl border border-slate-200/80 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Marketplaces</span>
            <span className="text-[12px] font-bold text-slate-700">{connectedCount}/{channels.length}</span>
          </div>
          {uniquePlatforms.length > 0 ? (
            <div className="space-y-2">
              {uniquePlatforms.map(p => {
                const pc = platformConfig[p];
                const pChannels = channels.filter(c => c.platform === p);
                const connected = pChannels.some(c => c.accountStatus === 'AUTHORIZED');
                return pc ? (
                  <div key={p} className="flex items-center gap-2.5">
                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${pc.gradient} flex items-center justify-center shadow-sm`}>
                      <span className={`text-[10px] font-bold ${pc.text}`}>{pc.letter}</span>
                    </div>
                    <span className="text-[12px] font-semibold text-slate-700 flex-1">{pc.label}</span>
                    {connected
                      ? <CheckCircle size={13} className="text-emerald-500" />
                      : <AlertCircle size={13} className="text-amber-400" />
                    }
                  </div>
                ) : null;
              })}
            </div>
          ) : (
            <p className="text-[12px] text-slate-400">No channels connected</p>
          )}
          <button
            onClick={() => navigate('/marketplaces')}
            className="w-full mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-[#C45500] bg-[#FFF4E6] hover:bg-[#FFE8C8] rounded-lg transition-colors border border-[#FFE8C8]"
          >
            <Plug size={11} /> Manage Channels <ChevronRight size={11} />
          </button>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-xl border border-slate-200/80 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Notifications</span>
            <Bell size={13} className="text-slate-300" />
          </div>
          <p className="text-[12px] text-slate-500 mb-3">Configure email, WhatsApp and SMS alerts for this seller.</p>
          <button
            onClick={() => navigate('/settings/notification-config')}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-[#C45500] bg-[#FFF4E6] hover:bg-[#FFE8C8] rounded-lg transition-colors border border-[#FFE8C8]"
          >
            <Bell size={11} /> Configure <ChevronRight size={11} />
          </button>
        </div>
      </div>

      {/* Remove User Confirmation */}
      {removeConfirm.open && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setRemoveConfirm({ open: false, userId: null, name: '' })} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="p-6">
              <div className="w-12 h-12 rounded-2xl bg-red-50 ring-4 ring-red-100 flex items-center justify-center mb-4"><Trash2 size={22} className="text-red-500" /></div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Remove User</h3>
              <p className="text-sm text-slate-500">Remove <span className="font-semibold text-slate-700">{removeConfirm.name}</span> from this seller? They will lose access to manage this account.</p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50/80 border-t border-slate-100">
              <button onClick={() => setRemoveConfirm({ open: false, userId: null, name: '' })} disabled={removeLoading} className="px-4 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-50 transition-all">Cancel</button>
              <button onClick={handleRemoveUser} disabled={removeLoading} className="px-5 py-2.5 text-sm font-semibold text-white rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 transition-all shadow-sm">
                {removeLoading && <Loader2 size={14} className="animate-spin" />}Remove
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Assign Modal */}
      {showAssignUserModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAssignUserModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[780px] max-w-[95vw] overflow-hidden flex flex-col" style={{ height: '80vh' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-8 py-6 border-b border-slate-200 flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">Assign Members to {seller.businessName}</h2>
                <p className="text-sm text-slate-400 mt-1">Assign roles and optionally add their contact info to notification recipients</p>
              </div>
              <button onClick={() => setShowAssignUserModal(false)} className="p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* User List */}
            <AssignUserList
              users={availableUsers}
              loading={users.length === 0 && usersLoadedRef.current}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              selectedRoles={selectedRoles}
              setSelectedRoles={setSelectedRoles}
              notifyFlags={notifyFlags}
              setNotifyFlags={setNotifyFlags}
              roleOptions={ROLE_OPTIONS}
            />

            {/* Footer */}
            <div className="flex items-center justify-between px-8 py-5 border-t border-slate-200 bg-slate-50/80 flex-shrink-0">
              <div className="text-sm text-slate-500">
                {selectedUsers.length > 0
                  ? <><span className="font-bold text-slate-700">{selectedUsers.length}</span> user{selectedUsers.length > 1 ? 's' : ''} selected</>
                  : 'No users selected'
                }
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowAssignUserModal(false)} className="px-5 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors">Cancel</button>
                <button
                  type="button"
                  onClick={e => { e.preventDefault(); e.stopPropagation(); handleAssign(); }}
                  disabled={assignLoading || selectedUsers.length === 0}
                  className="px-6 py-2.5 text-sm font-semibold text-white bg-[#232F3E] hover:bg-[#37475A] rounded-xl transition-colors disabled:opacity-40 flex items-center gap-2 shadow-sm"
                >
                  {assignLoading && <Loader2 size={15} className="animate-spin" />}
                  {assignLoading ? 'Assigning...' : `Assign ${selectedUsers.length > 0 ? selectedUsers.length + ' User' + (selectedUsers.length > 1 ? 's' : '') : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>, document.body
      )}
    </div>
  );
}

function AssignUserList({ users, loading, selectedUsers, setSelectedUsers, selectedRoles, setSelectedRoles, notifyFlags, setNotifyFlags, roleOptions }) {
  const [search, setSearch] = useState("");
  const filtered = (users || []).filter(user => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      user.name?.toLowerCase().includes(q) ||
      user.email?.toLowerCase().includes(q) ||
      user.phone?.toLowerCase().includes(q) ||
      (user.type && user.type.toLowerCase().includes(q))
    );
  });

  const toggleUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
      setSelectedRoles(prev => { const next = { ...prev }; delete next[userId]; return next; });
      setNotifyFlags(prev => { const next = { ...prev }; delete next[userId]; return next; });
    } else {
      setSelectedUsers(prev => [...prev, userId]);
      setSelectedRoles(prev => ({ ...prev, [userId]: 4 }));
      setNotifyFlags(prev => ({ ...prev, [userId]: false }));
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Search */}
      <div className="px-8 pt-5 pb-3">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email or phone..."
            className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] transition-all placeholder:text-slate-300"
          />
        </div>
      </div>

      {/* Table header */}
      {!loading && filtered.length > 0 && (
        <div className="px-8 pb-1">
          <div className="flex items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest px-4 py-2 border-b border-slate-100">
            <span className="w-7" />
            <span className="flex-1 ml-3">User</span>
            <span className="w-[150px] text-center">Role</span>
            <span className="w-[160px] text-center">Notification Recipient</span>
          </div>
        </div>
      )}

      {/* User list */}
      <div className="flex-1 overflow-y-auto px-6 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2">
            <Loader2 size={18} className="animate-spin text-[#f08804]" />
            <span className="text-sm text-slate-400">Loading users...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <Users size={24} className="text-slate-300" />
            </div>
            <p className="text-sm font-semibold text-slate-600">{search ? 'No matches found' : 'All users assigned'}</p>
            <p className="text-xs text-slate-400 mt-1">{search ? 'Try a different search term' : 'Every user has been assigned to this seller'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(user => {
              const isSelected = selectedUsers.includes(user.id);
              const initials = (user.name || '??').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
              const hasContact = user.email || user.phone;
              const colors = ['from-[#f08804] to-blue-600', 'from-emerald-500 to-teal-600', 'from-amber-500 to-orange-600', 'from-violet-500 to-purple-600', 'from-rose-500 to-pink-600'];
              const grad = colors[user.id?.charCodeAt(0) % colors.length] || colors[0];
              return (
                <div
                  key={user.id}
                  className={`rounded-xl border transition-all ${
                    isSelected
                      ? 'bg-[#FFF9EE] border-[#FFD89E]'
                      : 'border-slate-100 hover:bg-slate-50 hover:border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-3 px-4 py-3.5">
                    {/* Checkbox + User info */}
                    <button
                      type="button"
                      onClick={() => toggleUser(user.id)}
                      className="flex items-center gap-3.5 flex-1 min-w-0 text-left"
                    >
                      <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected ? 'bg-[#232F3E] border-[#C45500]' : 'border-slate-300 bg-white'
                      }`}>
                        {isSelected && <Check size={11} className="text-white" strokeWidth={3} />}
                      </div>
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <span className="text-[12px] font-bold text-white">{initials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{user.name || user.email}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          {user.email && <span className="text-[11px] text-slate-400 truncate flex items-center gap-1"><Mail size={9} className="text-slate-300 flex-shrink-0" />{user.email}</span>}
                          {user.phone && <span className="text-[11px] text-slate-400 flex items-center gap-1"><Phone size={9} className="text-slate-300 flex-shrink-0" />{user.phone}</span>}
                        </div>
                      </div>
                    </button>

                    {/* Role dropdown */}
                    {isSelected && (
                      <select
                        value={selectedRoles[user.id] || ''}
                        onChange={e => { e.stopPropagation(); setSelectedRoles(prev => ({ ...prev, [user.id]: Number(e.target.value) })); }}
                        onClick={e => e.stopPropagation()}
                        className="w-[150px] flex-shrink-0 px-3 py-2.5 text-[12px] font-semibold bg-white border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] transition-all cursor-pointer appearance-none"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: '28px' }}
                      >
                        {roleOptions.map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    )}

                    {/* Add to notification recipients toggle */}
                    {isSelected && (
                      <div className="w-[160px] flex-shrink-0 flex justify-center">
                        {!hasContact ? (
                          <span className="text-[10px] text-slate-300 italic">No contact info</span>
                        ) : (
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setNotifyFlags(prev => ({ ...prev, [user.id]: !prev[user.id] }));
                            }}
                            className="inline-flex items-center gap-2 group"
                            title={notifyFlags[user.id]
                              ? `Will add ${[user.email, user.phone].filter(Boolean).join(' & ')} to notification config`
                              : 'Enable to add contact info to notification recipients'
                            }
                          >
                            {/* Toggle switch */}
                            <div className={`relative w-9 h-5 rounded-full transition-colors ${notifyFlags[user.id] ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${notifyFlags[user.id] ? 'left-[18px]' : 'left-0.5'}`} />
                            </div>
                            <span className={`text-[11px] font-medium transition-colors ${notifyFlags[user.id] ? 'text-emerald-600' : 'text-slate-400'}`}>
                              {notifyFlags[user.id] ? 'Yes' : 'No'}
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 2: CHANNELS
// ═══════════════════════════════════════════════════════════════════════════

function ChannelsTab({ seller, onConnectChannel, onHealthCheck, onMarketplace, onConnectAds, onReconnect, onDisconnect, onDelete, healthCheckingId }) {
  const channels = seller.channels || [];

  if (channels.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FFE8C8] to-[#FFF4E6] flex items-center justify-center mx-auto mb-5 border border-[#FFE8C8]">
          <Plug size={28} className="text-[#f08804]" />
        </div>
        <h3 className="text-lg font-bold text-slate-700 mb-2">No marketplaces connected</h3>
        <p className="text-sm text-slate-400 mb-6 max-w-sm mx-auto">Connect a marketplace account to start receiving order data and sending notifications for this seller.</p>
        <button onClick={onConnectChannel} className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-b from-[#f7ca00] to-[#f0c14b] border border-[#a88734] text-[#0F1111] rounded-lg text-sm font-semibold hover:from-[#f0c14b] hover:to-[#e7ae00] transition-all shadow-sm">
          <Plus size={16} /> Connect First Channel
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 font-medium">{channels.length} marketplace{channels.length !== 1 ? 's' : ''} connected</p>
        <button onClick={onConnectChannel} className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-b from-[#f7ca00] to-[#f0c14b] border border-[#a88734] text-[#0F1111] rounded-lg hover:from-[#f0c14b] hover:to-[#e7ae00] text-xs font-semibold transition-all shadow-sm">
          <Plus size={14} /> Connect Marketplace
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3">
        {channels.map(ch => {
          const pc = platformConfig[ch.platform] || platformConfig.AMAZON;
          const sc = statusConfig[ch.accountStatus] || statusConfig.PENDING;
          const StatusIcon = sc.icon;
          const isChecking = healthCheckingId === ch.id;
          return (
            <div key={ch.id} className="bg-white rounded-xl border border-slate-200 hover:shadow-md transition-all">
              <div className="flex items-center gap-5 px-6 py-5">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pc.gradient} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                  <span className={`text-base font-bold ${pc.text}`}>{pc.letter}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 mb-0.5">
                    <span className="text-sm font-bold text-slate-800 truncate">{ch.sellerName || ch.sellingPartnerId || ch.platform}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${pc.badge}`}>{ch.platformLabel || ch.platform}</span>
                  </div>
                  {ch.marketplaceId && <span className="text-[11px] text-slate-400 font-mono">{ch.marketplaceId}</span>}
                </div>
                {isChecking ? (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#FFF4E6] text-[#C45500] border border-[#FFD89E] animate-pulse">
                    <Loader2 size={12} className="animate-spin" />Checking...
                  </div>
                ) : (
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${sc.bg} ${sc.color} border ${sc.border}`}>
                    <StatusIcon size={12} />{sc.label}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  {ch.platform === 'AMAZON' && (
                    <>
                      <button onClick={() => onHealthCheck(ch)} disabled={isChecking} title="Health Check" className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-50">
                        <HeartPulse size={15} />
                      </button>
                      <button onClick={() => onMarketplace(ch)} title="Marketplaces" className="p-2 rounded-lg text-slate-400 hover:text-[#C45500] hover:bg-[#FFF4E6] transition-colors"><Globe size={15} /></button>
                    </>
                  )}
                  {ch.platform === 'AMAZON' && ch.accountStatus === 'AUTHORIZED' && (
                    ch.adsAccountStatus === 'AUTHORIZED' ? (
                      <button onClick={() => onConnectAds(ch)} title="Amazon Ads connected — click to re-authorize" className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"><Megaphone size={12} /> Ads</button>
                    ) : (
                      <button onClick={() => onConnectAds(ch)} title="Connect Amazon Ads" className="p-2 rounded-lg text-slate-400 hover:text-[#C45500] hover:bg-[#FFF4E6] transition-colors"><Megaphone size={15} /></button>
                    )
                  )}
                  {(ch.accountStatus === 'REVOKED' || ch.accountStatus === 'ERROR') && (
                    <button onClick={() => onReconnect(ch)} title="Reconnect" className="p-2 rounded-lg text-slate-400 hover:text-[#C45500] hover:bg-[#FFF4E6] transition-colors"><PlugZap size={15} /></button>
                  )}
                  {ch.accountStatus === 'AUTHORIZED' && (
                    <button onClick={() => onDisconnect(ch)} title="Disconnect" className="p-2 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"><Unplug size={15} /></button>
                  )}
                  <button onClick={() => onDelete(ch)} title="Delete" className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 3: NOTIFICATION CONFIG
// ═══════════════════════════════════════════════════════════════════════════

// API map: notification type → report endpoint
const REPORT_API_MAP = {
  DAILY_ORDER_SALES: { endpoint: ENDPOINTS.REPORTS.DAILY_SUMMARY, label: 'Daily Summary' },
  PRODUCT_LISTING_STATUS: { endpoint: ENDPOINTS.REPORTS.LISTING_STATUS, label: 'Listing Status' },
  FEEDBACK_REVIEW: { endpoint: ENDPOINTS.REPORTS.FEEDBACK, label: 'Feedback Check' },
  PRODUCT_HIJACK: { endpoint: ENDPOINTS.REPORTS.HIJACK_CHECK, label: 'Hijack Check' },
  POLICY_WARNING: { endpoint: ENDPOINTS.POLICY_WARNINGS.SYNC, label: 'Policy Check' },
  SPONSORED_AD_PERFORMANCE: { endpoint: ENDPOINTS.AD_PERFORMANCE.RUN_NOW, label: 'Ad Performance' },
  REVIEW_REQUEST: { endpoint: ENDPOINTS.FEEDBACK_REQUESTS.GENERATE_NOW, label: 'Test Review Request' },
};

// ─── Config <-> DTO Mapping ──────────────────────────────────────────────────

const DAY_NAME_TO_NUM = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7 };
const DAY_NUM_TO_NAME = Object.fromEntries(Object.entries(DAY_NAME_TO_NUM).map(([k, v]) => [v, k]));

function toBackendDTO(config, notificationType) {
  // Convert 12h → 24h time string "HH:mm"
  let hour24 = config.schedule.hour;
  if (config.schedule.period === 'PM' && hour24 !== 12) hour24 += 12;
  if (config.schedule.period === 'AM' && hour24 === 12) hour24 = 0;
  const scheduleTime = `${String(hour24).padStart(2, '0')}:${config.schedule.minute}`;

  // Map channels object → array
  const channels = [];
  for (const [type, ch] of Object.entries(config.channels)) {
    channels.push({
      channelType: type.toUpperCase(),
      isEnabled: ch.enabled,
      recipients: JSON.stringify(ch.recipients || []),
      templateId: null,
    });
  }

  return {
    notificationType,
    isEnabled: config.isEnabled,
    scheduleFrequency: config.schedule.frequency,
    scheduleTime,
    scheduleTimezone: 'Asia/Kolkata',
    scheduleDayOfWeek: DAY_NAME_TO_NUM[config.schedule.dayOfWeek] || null,
    scheduleDayOfMonth: config.schedule.dayOfMonth || null,
    dataRange: config.data.range,
    grouping: config.data.grouping,
    includeEmptyReports: config.data.includeEmpty,
    retryAttempts: config.advanced.retryAttempts,
    quietHoursEnabled: config.advanced.quietHoursEnabled,
    quietHoursStart: config.advanced.quietHoursStart || null,
    quietHoursEnd: config.advanced.quietHoursEnd || null,
    thresholdEnabled: config.advanced.thresholdEnabled,
    thresholdCondition: config.advanced.thresholdCondition,
    priority: config.advanced.priority,
    channels,
  };
}

function fromBackendDTO(dto) {
  // Parse "HH:mm" → 12h parts
  let hour24 = 9, minute = '00', period = 'AM';
  if (dto.scheduleTime) {
    const parts = dto.scheduleTime.split(':');
    hour24 = parseInt(parts[0], 10);
    minute = parts[1] || '00';
    if (!['00', '15', '30', '45'].includes(minute)) minute = '00';
    period = hour24 >= 12 ? 'PM' : 'AM';
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    hour24 = hour12; // reuse for display
  }

  // Map channels array → object
  const channels = {
    whatsapp: { enabled: false, recipients: [] },
    email: { enabled: false, recipients: [] },
    sms: { enabled: false, recipients: [] },
  };
  if (dto.channels) {
    for (const ch of dto.channels) {
      const key = (ch.channelType || '').toLowerCase();
      if (channels[key] !== undefined) {
        channels[key].enabled = ch.isEnabled || false;
        try {
          channels[key].recipients = typeof ch.recipients === 'string' ? JSON.parse(ch.recipients) : (ch.recipients || []);
        } catch { channels[key].recipients = []; }
      }
    }
  }

  return {
    isEnabled: dto.isEnabled || false,
    schedule: {
      frequency: dto.scheduleFrequency || 'daily',
      hour: hour24,
      minute,
      period,
      dayOfWeek: DAY_NUM_TO_NAME[dto.scheduleDayOfWeek] || 'Monday',
      dayOfMonth: dto.scheduleDayOfMonth || 1,
    },
    data: {
      range: dto.dataRange || '1',
      grouping: dto.grouping || 'combined',
      includeEmpty: dto.includeEmptyReports || false,
    },
    channels,
    advanced: {
      retryAttempts: dto.retryAttempts || 3,
      quietHoursEnabled: dto.quietHoursEnabled || false,
      quietHoursStart: dto.quietHoursStart || '22:00',
      quietHoursEnd: dto.quietHoursEnd || '07:00',
      thresholdEnabled: dto.thresholdEnabled || false,
      thresholdCondition: dto.thresholdCondition || 'orders_gt_0',
      priority: dto.priority || 'normal',
    },
    lastRun: dto.lastRunAt || null,
    nextRun: dto.nextRunAt || null,
    _backendId: dto.id || null,
  };
}

function NotificationsTab({ seller }) {
  const [selectedType, setSelectedType] = useState(NOTIFICATION_TYPES[0].id);
  const [configs, setConfigs] = useState(() => {
    const c = {};
    NOTIFICATION_TYPES.forEach(t => { c[t.id] = JSON.parse(JSON.stringify(DEFAULT_CONFIG)); });
    c['DAILY_ORDER_SALES'].isEnabled = true;
    c['DAILY_ORDER_SALES'].channels.whatsapp = { enabled: true, recipients: [seller.contactPhone || '+91 98765 43210'] };
    c['DAILY_ORDER_SALES'].channels.email = { enabled: true, recipients: [seller.contactEmail || 'seller@example.com'] };
    c['FEEDBACK_REVIEW'].schedule.frequency = 'every_2_hours';
    return c;
  });
  const [saving, setSaving] = useState(false);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);

  // Load saved configs from backend on mount
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const res = await apiClient.get(ENDPOINTS.NOTIFICATION_CONFIGS.GET_BY_SELLER(seller.id));
        const savedConfigs = res.data || res;
        if (Array.isArray(savedConfigs) && savedConfigs.length > 0) {
          setConfigs(prev => {
            const next = { ...prev };
            for (const dto of savedConfigs) {
              if (dto.notificationType && next[dto.notificationType] !== undefined) {
                next[dto.notificationType] = fromBackendDTO(dto);
              }
            }
            return next;
          });
        }
      } catch (err) {
        // If 404 or no configs yet, that's fine — use defaults
        if (err?.response?.status !== 404) {
          console.warn('Failed to load notification configs:', err);
        }
      } finally {
        setLoadingConfigs(false);
      }
    };
    loadConfigs();
  }, [seller.id]);

  // Get connected accounts for this seller
  const connectedAccounts = (seller.channels || []).filter(c => c.accountStatus === 'AUTHORIZED');

  const handleGenerate = async (accountId) => {
    const apiConfig = REPORT_API_MAP[selectedType];
    if (!apiConfig) {
      toast.error('Report generation not yet available for this type');
      return;
    }
    setAccountPickerOpen(false);
    setGenerating(true);
    setReportResult(null);
    try {
      const res = await apiClient.post(`${apiConfig.endpoint}?sellerAccountId=${accountId}`);
      const data = res.data || res;
      setReportResult({ success: true, data, type: selectedType });
      toast.success(`${apiConfig.label} generated successfully`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Report generation failed';
      setReportResult({ success: false, error: msg, type: selectedType });
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const triggerGenerate = () => {
    if (connectedAccounts.length === 0) {
      toast.error('No connected seller accounts. Connect a channel first.');
      return;
    }
    if (connectedAccounts.length === 1) {
      handleGenerate(connectedAccounts[0].id);
    } else {
      setAccountPickerOpen(true);
    }
  };

  const activeType = NOTIFICATION_TYPES.find(t => t.id === selectedType);
  const config = configs[selectedType] || DEFAULT_CONFIG;

  const updateConfig = (path, value) => {
    setConfigs(prev => {
      const next = { ...prev };
      const c = JSON.parse(JSON.stringify(next[selectedType]));
      const keys = path.split('.');
      let obj = c;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      next[selectedType] = c;
      return next;
    });
  };

  // ─── FIX: Always reads fresh state for recipient mutations ───────────────
  // Using setConfigs with a functional updater avoids stale closure issues
  // where config.channels.X.recipients could be an outdated snapshot when
  // onAdd/onRemove fires after a channel toggle or rapid keystrokes.
  const updateRecipients = useCallback((channel, action, tagOrIndex) => {
    setConfigs(prev => {
      const next = { ...prev };
      const c = JSON.parse(JSON.stringify(next[selectedType]));
      const current = c.channels[channel].recipients;
      c.channels[channel].recipients = action === 'add'
        ? [...current, tagOrIndex]
        : current.filter((_, j) => j !== tagOrIndex);
      next[selectedType] = c;
      return next;
    });
  }, [selectedType]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Read the latest config directly from state to avoid any stale closure
      setConfigs(prev => {
        const latestConfig = prev[selectedType];
        const dto = toBackendDTO(latestConfig, selectedType);
        const endpoint = ENDPOINTS.NOTIFICATION_CONFIGS.SAVE(seller.id, selectedType);
        apiClient.post(endpoint, dto)
          .then(res => {
            const savedDTO = res.data || res;
            setConfigs(p => ({ ...p, [selectedType]: fromBackendDTO(savedDTO) }));
            setIsEditing(false);
            toast.success(`${activeType.shortTitle} config saved`);
          })
          .catch(err => {
            const msg = err?.response?.data?.message || err?.message || 'Failed to save config';
            toast.error(msg);
          })
          .finally(() => setSaving(false));
        return prev; // no state change here, just reading
      });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save config';
      toast.error(msg);
      setSaving(false);
    }
  };

  const handleTest = async () => {
    // Determine which seller account to use (first connected channel)
    const channels = seller.channels || [];
    const account = channels.find(c => c.accountStatus === 'AUTHORIZED') || channels[0];
    if (!account) {
      toast.error('No marketplace connected — connect a channel first');
      return;
    }
    const sellerAccountId = account.id;

    // Map notification types to their Run Now API endpoints
    const runNowEndpoints = {
      DAILY_ORDER_SALES: { url: ENDPOINTS.REPORTS.DAILY_SUMMARY, params: { sellerAccountId } },
      PRODUCT_LISTING_STATUS: { url: ENDPOINTS.REPORTS.LISTING_STATUS, params: { sellerAccountId } },
      FEEDBACK_REVIEW: { url: ENDPOINTS.REPORTS.FEEDBACK, params: { sellerAccountId } },
      PRODUCT_HIJACK: { url: ENDPOINTS.REPORTS.HIJACK_CHECK, params: { sellerAccountId } },
      POLICY_WARNING: { url: ENDPOINTS.POLICY_WARNINGS.SYNC, params: { sellerAccountId } },
      SPONSORED_AD_PERFORMANCE: { url: ENDPOINTS.AD_PERFORMANCE.RUN_NOW, params: { sellerAccountId } },
      REVIEW_REQUEST: { url: ENDPOINTS.FEEDBACK_REQUESTS.GENERATE_NOW, params: { sellerAccountId } },
    };

    const endpoint = runNowEndpoints[selectedType];
    if (!endpoint) {
      toast.error(`Run Now not available for ${activeType.shortTitle}`);
      return;
    }

    toast.promise(
      apiClient.post(endpoint.url, null, { params: endpoint.params }),
      {
        loading: `Running ${activeType.shortTitle}...`,
        success: `${activeType.shortTitle} completed — check Delivery Activity for results`,
        error: (err) => `Failed: ${err?.response?.data?.message || err.message}`,
      }
    );
  };

  const handleReset = async () => {
    const hadBackendId = config._backendId;
    setConfigs(prev => ({ ...prev, [selectedType]: JSON.parse(JSON.stringify(DEFAULT_CONFIG)) }));
    if (hadBackendId) {
      try {
        await apiClient.delete(ENDPOINTS.NOTIFICATION_CONFIGS.DELETE(seller.id, selectedType));
      } catch { /* ignore — local reset is sufficient */ }
    }
    toast.success('Reset to defaults');
  };

  const colorClasses = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', dot: 'bg-emerald-500', activeBg: 'bg-emerald-100' },
    blue: { bg: 'bg-[#FFF4E6]', text: 'text-[#C45500]', border: 'border-[#FFD89E]', dot: 'bg-[#232F3E]', activeBg: 'bg-[#FFE8C8]' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', dot: 'bg-amber-500', activeBg: 'bg-amber-100' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200', dot: 'bg-rose-500', activeBg: 'bg-rose-100' },
    violet: { bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200', dot: 'bg-violet-500', activeBg: 'bg-violet-100' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', dot: 'bg-orange-500', activeBg: 'bg-orange-100' },
    indigo: { bg: 'bg-[#FFF4E6]', text: 'text-[#C45500]', border: 'border-[#FFD89E]', dot: 'bg-[#FFF4E6]0', activeBg: 'bg-[#FFE8C8]' },
  };

  const inputCls = "w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:border-transparent focus:bg-white transition-colors";
  const selectCls = `${inputCls} bg-white`;
  const labelCls = "block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5";

  // Label maps
  const dataRangeLabel = { '1': 'Last 1 day', '3': 'Last 3 days', '7': 'Last 7 days', '14': 'Last 14 days', '30': 'Last 30 days', '60': 'Last 60 days', '90': 'Last 90 days' };
  const frequencyLabel = { every_1_hour: 'Every 1 Hour', every_2_hours: 'Every 2 Hours', every_4_hours: 'Every 4 Hours', every_6_hours: 'Every 6 Hours', daily: 'Daily', weekly: 'Weekly', biweekly: 'Bi-Weekly', monthly: 'Monthly' };
  const isHourly = ['every_1_hour', 'every_2_hours', 'every_4_hours', 'every_6_hours'].includes(config.schedule.frequency);
  const hourlyRangeLabel = { every_1_hour: 'Last 1 hour', every_2_hours: 'Last 2 hours', every_4_hours: 'Last 4 hours', every_6_hours: 'Last 6 hours' };
  const priorityLabel = { normal: 'Normal', high: 'High', critical: 'Critical' };
  const thresholdLabel = { orders_gt_0: 'Only if orders > 0', sales_drop_10: 'Sales drop > 10%', sales_drop_25: 'Sales drop > 25%', returns_gt_5: 'Returns > 5' };

  // Disabled state — when notification type is not enabled
  const isDisabled = !config.isEnabled;

  return (
    <div className="flex gap-6 items-start">
      {/* Left: Type Selector */}
      <div className="w-56 flex-shrink-0 space-y-1.5">
        <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-3">Notification Types</h4>
        {NOTIFICATION_TYPES.map(type => {
          const Icon = type.icon;
          const isActive = selectedType === type.id;
          const isOn = configs[type.id]?.isEnabled;
          const cc = colorClasses[type.color];
          return (
            <button key={type.id} onClick={() => { if (type.enabled) { setSelectedType(type.id); setIsEditing(false); } }} disabled={!type.enabled}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all relative ${isActive ? 'bg-white shadow-sm border border-slate-200' : 'hover:bg-white/60 border border-transparent'} ${!type.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-7 bg-[#232F3E] rounded-r-full" />}
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? cc.activeBg : 'bg-slate-100'} transition-colors`}>
                <Icon size={14} className={isActive ? cc.text : 'text-slate-400'} />
              </div>
              <div className="flex-1 min-w-0">
                <span className={`text-xs font-semibold block truncate ${isActive ? 'text-slate-800' : 'text-slate-600'}`}>{type.shortTitle}</span>
                {!type.enabled && <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 mt-0.5 inline-block">Soon</span>}
              </div>
              {type.enabled && isOn && <div className={`w-2 h-2 rounded-full ${cc.dot} flex-shrink-0`} />}
            </button>
          );
        })}
      </div>

      {/* Right: Config Panel */}
      <div className="flex-1 min-w-0 space-y-5">
        {/* Header Card — always interactive (master toggle + edit button) */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-5">
            <div className="flex items-center gap-4">
              {activeType && (() => {
                const Icon = activeType.icon;
                const cc = colorClasses[activeType.color];
                return <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${cc.bg} ${cc.border}`}><Icon size={20} className={cc.text} /></div>;
              })()}
              <div>
                <h3 className="text-base font-bold text-slate-900">{activeType?.title}</h3>
                <p className="text-xs text-slate-400 mt-0.5 leading-relaxed max-w-xl">{activeType?.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className={`text-xs font-bold ${config.isEnabled ? 'text-emerald-600' : 'text-slate-400'}`}>{config.isEnabled ? 'Enabled' : 'Disabled'}</span>
                <Toggle
                  checked={config.isEnabled}
                  onChange={async (v) => {
                    updateConfig('isEnabled', v);
                    if (config._backendId) {
                      try {
                        await apiClient.patch(ENDPOINTS.NOTIFICATION_CONFIGS.UPDATE_STATUS(seller.id, selectedType), null, { params: { enabled: v } });
                        toast.success(`${activeType.shortTitle} ${v ? 'enabled' : 'disabled'} successfully`);
                      } catch (err) {
                        updateConfig('isEnabled', !v);
                        toast.error('Failed to update status on server');
                      }
                    } else if (v) {
                      toast.success('Enabled locally. Please click "Save" to finalize setup.');
                    }
                  }}
                  size="lg"
                />
              </div>
              {/* Edit button */}
              {!isEditing && !isDisabled && (
                <button onClick={() => setIsEditing(true)} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-slate-600 bg-white hover:bg-slate-50 rounded-lg transition-colors border border-slate-200">
                  <Pencil size={12} /> Edit
                </button>
              )}
            </div>
          </div>

          {/* Last/Next Run + Run Now */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-slate-50/50">
            <div className="flex items-center gap-6">
              {config.lastRun && <span className="text-[11px] text-slate-400 flex items-center gap-1.5"><Clock size={11} /> Last: {new Date(config.lastRun).toLocaleString('en-IN')}</span>}
              {config.nextRun && <span className="text-[11px] text-[#f08804] font-medium flex items-center gap-1.5"><Calendar size={11} /> Next: {new Date(config.nextRun).toLocaleString('en-IN')}</span>}
              {!config.lastRun && !config.nextRun && <span className="text-[11px] text-slate-300">No schedule configured</span>}
            </div>
            <div className="relative">
              {REPORT_API_MAP[selectedType] ? (
                <>
                  <button onClick={triggerGenerate} disabled={generating || isDisabled}
                    title={isDisabled ? 'Enable this notification first' : 'Run a test report now'}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm shadow-emerald-200">
                    {generating ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                    {generating ? 'Running...' : 'Run Now'}
                  </button>
                  {/* Account Picker Dropdown */}
                  {accountPickerOpen && connectedAccounts.length > 1 && (
                    <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl border border-slate-200 shadow-xl z-30 overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/80">
                        <p className="text-xs font-bold text-slate-700">Select Seller Account</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Choose which account to run the report for</p>
                      </div>
                      <div className="p-2 max-h-48 overflow-y-auto">
                        {connectedAccounts.map(acc => {
                          const pc = platformConfig[acc.platform] || platformConfig.AMAZON;
                          return (
                            <button key={acc.id} onClick={() => handleGenerate(acc.id)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left">
                              <div className={`w-7 h-7 rounded-md bg-gradient-to-br ${pc.gradient} flex items-center justify-center flex-shrink-0`}>
                                <span className={`text-[10px] font-bold ${pc.text}`}>{pc.letter}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="text-xs font-semibold text-slate-700 block truncate">{acc.sellerName || acc.sellingPartnerId}</span>
                                {acc.marketplaceId && <span className="text-[10px] text-slate-400 font-mono">{acc.marketplaceId}</span>}
                              </div>
                              <Zap size={11} className="text-emerald-400 flex-shrink-0" />
                            </button>
                          );
                        })}
                      </div>
                      <div className="px-3 py-2 border-t border-slate-100">
                        <button onClick={() => setAccountPickerOpen(false)} className="w-full text-[11px] text-slate-400 hover:text-slate-600 font-medium py-1">Cancel</button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-[10px] font-semibold text-slate-300 bg-slate-50 px-3 py-2 rounded-lg border border-slate-100">API Coming Soon</span>
              )}
            </div>
          </div>
        </div>

        {/* Report Result Panel */}
        {reportResult && reportResult.type === selectedType && (
          <div className={`rounded-xl border overflow-hidden ${reportResult.success ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30'}`}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100/50">
              <div className="flex items-center gap-2.5">
                {reportResult.success
                  ? <CheckCircle size={15} className="text-emerald-600" />
                  : <AlertCircle size={15} className="text-red-600" />
                }
                <span className={`text-xs font-bold ${reportResult.success ? 'text-emerald-800' : 'text-red-800'}`}>
                  {reportResult.success ? 'Report Generated Successfully' : 'Report Generation Failed'}
                </span>
              </div>
              <button onClick={() => setReportResult(null)} className="p-1 hover:bg-white/80 rounded-md transition-colors">
                <X size={13} className="text-slate-400" />
              </button>
            </div>
            {reportResult.success ? (
              <div className="px-5 py-4">
                {reportResult.type === 'DAILY_ORDER_SALES' && reportResult.data && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Daily Summary</span>
                      {reportResult.data.sellerName && <span className="text-[10px] font-semibold text-[#C45500] bg-[#FFF4E6] px-2 py-0.5 rounded-full border border-[#FFE8C8]">{reportResult.data.sellerName}</span>}
                      {reportResult.data.syncStatus && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${reportResult.data.syncStatus === 'SUCCESS' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                          {reportResult.data.syncStatus}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <ReportMetric label="Units Ordered" value={reportResult.data.unitsOrdered ?? '—'} icon={Package} color="blue" />
                      <ReportMetric label="Total Sales" value={reportResult.data.totalSales != null ? `₹${Number(reportResult.data.totalSales).toLocaleString('en-IN')}` : '—'} icon={TrendingUp} color="emerald" />
                      <ReportMetric label="Shipped Units" value={reportResult.data.shippedUnits ?? '—'} icon={Send} color="indigo" />
                      <ReportMetric label="Returns" value={reportResult.data.returnsReceived ?? '—'} icon={RotateCcw} color="rose" />
                    </div>
                    {reportResult.data.syncMessage && (
                      <p className="text-[11px] text-slate-400 mt-2 italic">{reportResult.data.syncMessage}</p>
                    )}
                  </div>
                )}
                {reportResult.type === 'PRODUCT_LISTING_STATUS' && reportResult.data && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Listing Status</span>
                      {reportResult.data.syncStatus && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${reportResult.data.syncStatus === 'SUCCESS' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                          {reportResult.data.syncStatus}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <ReportMetric label="Total Listings" value={reportResult.data.totalListings ?? '—'} icon={Package} color="slate" />
                      <ReportMetric label="Active" value={reportResult.data.activeListings ?? '—'} icon={CheckCircle} color="emerald" />
                      <ReportMetric label="Inactive" value={reportResult.data.inactiveListings ?? '—'} icon={XCircle} color="amber" />
                      <ReportMetric label="Out of Stock" value={reportResult.data.outOfStockListings ?? '—'} icon={AlertTriangle} color="rose" />
                    </div>
                    {(reportResult.data.inactiveItems?.length > 0 || reportResult.data.outOfStockItems?.length > 0) && (
                      <div className="mt-3 space-y-2">
                        {reportResult.data.inactiveItems?.length > 0 && (
                          <ListingItemsTable title="Inactive Listings" items={reportResult.data.inactiveItems} color="amber" />
                        )}
                        {reportResult.data.outOfStockItems?.length > 0 && (
                          <ListingItemsTable title="Out of Stock" items={reportResult.data.outOfStockItems} color="rose" />
                        )}
                      </div>
                    )}
                  </div>
                )}
                {reportResult.type === 'FEEDBACK_REVIEW' && reportResult.data && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Seller Feedback</span>
                      {reportResult.data.sellerName && <span className="text-[10px] font-semibold text-[#C45500] bg-[#FFF4E6] px-2 py-0.5 rounded-full border border-[#FFE8C8]">{reportResult.data.sellerName}</span>}
                      {reportResult.data.syncStatus && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${reportResult.data.syncStatus === 'SUCCESS' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                          {reportResult.data.syncStatus}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <ReportMetric label="Total Feedback" value={reportResult.data.totalFeedback ?? '—'} icon={Star} color="slate" />
                      <ReportMetric label="Positive (4-5★)" value={reportResult.data.positiveCount ?? '—'} icon={CheckCircle} color="emerald" />
                      <ReportMetric label="Neutral (3★)" value={reportResult.data.neutralCount ?? '—'} icon={AlertCircle} color="amber" />
                      <ReportMetric label="Negative (1-2★)" value={reportResult.data.negativeCount ?? '—'} icon={AlertTriangle} color="rose" />
                    </div>
                    {reportResult.data.newFeedbackCount > 0 && (
                      <div className="mt-3">
                        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
                            <span className="text-[11px] font-bold text-[#C45500]">New Feedback ({reportResult.data.newFeedbackCount})</span>
                          </div>
                          <div className="divide-y divide-slate-50">
                            {reportResult.data.newFeedback?.map((fb, i) => (
                              <div key={i} className="px-4 py-3">
                                <div className="flex items-center gap-3 mb-1">
                                  <span className="font-mono text-[10px] text-slate-400">{fb.amazonOrderId || '—'}</span>
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${fb.classification === 'POSITIVE' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : fb.classification === 'NEUTRAL' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                                    {'★'.repeat(fb.rating)} {fb.classification}
                                  </span>
                                  {fb.feedbackDate && <span className="text-[10px] text-slate-400">{fb.feedbackDate}</span>}
                                </div>
                                {fb.comments && <p className="text-[11px] text-slate-600 mt-1">"{fb.comments}"</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    {reportResult.data.newFeedbackCount === 0 && (
                      <p className="text-[11px] text-slate-400 italic mt-1">No new feedback found since last check</p>
                    )}
                    {reportResult.data.syncMessage && (
                      <p className="text-[11px] text-slate-400 mt-1 italic">{reportResult.data.syncMessage}</p>
                    )}
                  </div>
                )}

                {/* ── Hijack Check Results ── */}
                {reportResult.type === 'PRODUCT_HIJACK' && reportResult.data && (
                  <div className="px-5 pb-5 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {reportResult.data.syncStatus && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${reportResult.data.syncStatus === 'SUCCESS' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                          {reportResult.data.syncStatus}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                      <ReportMetric label="Total Checked" value={reportResult.data.totalAsinsChecked ?? '—'} icon={Package} color="slate" />
                      <ReportMetric label="Safe" value={reportResult.data.safeCount ?? '—'} icon={CheckCircle} color="emerald" />
                      <ReportMetric label="Hijacked (BB Retained)" value={reportResult.data.hijackedBuyBoxRetainedCount ?? '—'} icon={AlertCircle} color="amber" />
                      <ReportMetric label="Hijacked (BB Lost)" value={reportResult.data.hijackedBuyBoxLostCount ?? '—'} icon={AlertTriangle} color="rose" />
                      <ReportMetric label="BB Suppressed" value={reportResult.data.buyBoxSuppressedCount ?? '—'} icon={XCircle} color="violet" />
                    </div>
                    {reportResult.data.results?.length > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle size={13} className="text-rose-500" />
                          <span className="text-[11px] font-bold text-rose-700">Alerts ({reportResult.data.results.length})</span>
                        </div>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {reportResult.data.results.map((item, i) => {
                            const statusColors = {
                              HIJACKED_BUYBOX_RETAINED: 'bg-amber-50 text-amber-700 border-amber-200',
                              HIJACKED_BUYBOX_LOST: 'bg-rose-50 text-rose-700 border-rose-200',
                              BUYBOX_SUPPRESSED: 'bg-violet-50 text-violet-700 border-violet-200',
                            };
                            const statusLabels = {
                              HIJACKED_BUYBOX_RETAINED: 'BB Retained',
                              HIJACKED_BUYBOX_LOST: 'BB Lost',
                              BUYBOX_SUPPRESSED: 'Suppressed',
                            };
                            return (
                              <div key={i} className="bg-white border border-slate-100 rounded-lg p-3 flex items-start gap-3">
                                {item.imageUrl && (
                                  <img src={item.imageUrl} alt="" className="w-10 h-10 rounded object-cover border border-slate-200 flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[10px] font-mono text-slate-500">{item.asin}</span>
                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${statusColors[item.hijackStatus] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                                      {statusLabels[item.hijackStatus] || item.hijackStatus}
                                    </span>
                                  </div>
                                  <p className="text-[11px] text-slate-700 truncate">{item.title || 'Untitled'}</p>
                                  <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                                    <span>Your: ₹{item.yourPrice ?? '—'}</span>
                                    <span>BB: ₹{item.buyBoxPrice ?? '—'}</span>
                                    {item.priceDifferencePercent != null && (
                                      <span className={item.priceDifferencePercent > 0 ? 'text-rose-600 font-bold' : 'text-emerald-600'}>
                                        {item.priceDifferencePercent > 0 ? '+' : ''}{Number(item.priceDifferencePercent).toFixed(1)}%
                                      </span>
                                    )}
                                    <span>Offers: {item.totalOfferCount ?? '—'}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {(!reportResult.data.results || reportResult.data.results.length === 0) && reportResult.data.totalAsinsChecked > 0 && (
                      <p className="text-[11px] text-emerald-600 font-medium mt-1">All ASINs are safe — no hijack alerts.</p>
                    )}
                    {reportResult.data.syncMessage && (
                      <p className="text-[11px] text-slate-400 mt-1 italic">{reportResult.data.syncMessage}</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="px-5 py-4">
                <p className="text-xs text-red-600">{reportResult.error}</p>
              </div>
            )}
          </div>
        )}

        {/* Config Sections — disabled overlay when not enabled */}
        <div className={`relative ${isDisabled ? 'pointer-events-none select-none' : ''}`}>
          {isDisabled && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 rounded-xl flex items-center justify-center">
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-6 py-4 text-center">
                <Bell size={20} className="text-slate-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-slate-500">Enable this notification to configure</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Toggle the switch above to get started</p>
              </div>
            </div>
          )}

          {loadingConfigs ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={20} className="animate-spin text-[#f08804] mr-2" />
              <span className="text-sm text-slate-400">Loading configuration...</span>
            </div>
          ) : isEditing ? (
            /* ══ EDIT MODE ══ */
            <div className="space-y-5">
              {/* Schedule */}
              <ConfigSection title="Schedule" icon={Calendar} iconColor="text-[#C45500]" iconBg="bg-[#FFF4E6]" iconBorder="border-[#FFE8C8]">
                <div className="flex items-end gap-6 flex-wrap">
                  <div>
                    <label className={labelCls}>Frequency</label>
                    <SegmentedControl options={[{ value: 'every_1_hour', label: '1h' }, { value: 'every_2_hours', label: '2h' }, { value: 'every_4_hours', label: '4h' }, { value: 'every_6_hours', label: '6h' }, { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'biweekly', label: 'Bi-Weekly' }, { value: 'monthly', label: 'Monthly' }]} value={config.schedule.frequency} onChange={(v) => {
                      updateConfig('schedule.frequency', v);
                      const rangeDefaults = { every_1_hour: '1', every_2_hours: '1', every_4_hours: '1', every_6_hours: '1', daily: '1', weekly: '7', biweekly: '14', monthly: '30' };
                      updateConfig('data.range', rangeDefaults[v] || '1');
                    }} />
                  </div>
                  {!isHourly && (
                    <div>
                      <label className={labelCls}>Time</label>
                      <TimePicker hour={config.schedule.hour} minute={config.schedule.minute} period={config.schedule.period} onChange={({ hour, minute, period }) => { updateConfig('schedule.hour', hour); updateConfig('schedule.minute', minute); updateConfig('schedule.period', period); }} />
                    </div>
                  )}
                  {isHourly && (
                    <div>
                      <label className={labelCls}>Data Range</label>
                      <span className="inline-flex items-center px-3.5 py-1.5 bg-[#FFF4E6] text-[#C45500] rounded-lg text-[11px] font-bold border border-[#FFE8C8]">{hourlyRangeLabel[config.schedule.frequency]}</span>
                      <p className="text-[10px] text-slate-400 mt-1">Auto-matched to frequency</p>
                    </div>
                  )}
                  {(config.schedule.frequency === 'weekly' || config.schedule.frequency === 'biweekly') && (
                    <div>
                      <label className={labelCls}>Day of Week</label>
                      <select value={config.schedule.dayOfWeek} onChange={(e) => updateConfig('schedule.dayOfWeek', e.target.value)} className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:border-transparent transition-colors w-[130px]">
                        {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  )}
                  {config.schedule.frequency === 'monthly' && (
                    <div>
                      <label className={labelCls}>Day of Month</label>
                      <select value={config.schedule.dayOfMonth} onChange={(e) => updateConfig('schedule.dayOfMonth', Number(e.target.value))} className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:border-transparent transition-colors w-[72px]">
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              </ConfigSection>

              {/* Data Configuration */}
              <ConfigSection title="Data Configuration" icon={BarChart3} iconColor="text-violet-600" iconBg="bg-violet-50" iconBorder="border-violet-100">
                <div className="flex items-end gap-6 flex-wrap">
                  {!isHourly && (
                    <div>
                      <label className={labelCls}>Data Range</label>
                      <select value={config.data.range} onChange={(e) => updateConfig('data.range', e.target.value)} className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:border-transparent transition-colors w-[140px]">
                        {config.schedule.frequency === 'daily' && <option value="1">Last 1 day</option>}
                        <option value="3">Last 3 days</option>
                        <option value="7">Last 7 days</option>
                        <option value="14">Last 14 days</option>
                        <option value="30">Last 30 days</option>
                        {config.schedule.frequency === 'monthly' && <option value="60">Last 60 days</option>}
                        {config.schedule.frequency === 'monthly' && <option value="90">Last 90 days</option>}
                      </select>
                    </div>
                  )}
                  {isHourly && (
                    <div>
                      <label className={labelCls}>Data Range</label>
                      <span className="inline-flex items-center px-3.5 py-1.5 bg-violet-50 text-violet-700 rounded-lg text-[11px] font-bold border border-violet-100">{hourlyRangeLabel[config.schedule.frequency]}</span>
                    </div>
                  )}
                  <div>
                    <label className={labelCls}>Report Grouping</label>
                    <SegmentedControl options={[{ value: 'combined', label: 'Combined' }, { value: 'individual', label: 'Per Channel' }]} value={config.data.grouping} onChange={(v) => updateConfig('data.grouping', v)} />
                  </div>
                  {!isHourly && (
                    <label className="flex items-center gap-2.5 cursor-pointer bg-slate-50 rounded-lg px-3.5 py-1.5 border border-slate-100">
                      <Toggle checked={config.data.includeEmpty} onChange={(v) => updateConfig('data.includeEmpty', v)} size="sm" />
                      <span className="text-[11px] font-semibold text-slate-600">Include empty reports</span>
                    </label>
                  )}
                </div>
              </ConfigSection>

              {/* Delivery Channels */}
              <ConfigSection title="Delivery Channels" icon={Send} iconColor="text-emerald-600" iconBg="bg-emerald-50" iconBorder="border-emerald-100">
                <div className="space-y-4">
                  <ChannelConfigRow label="WhatsApp" icon={Smartphone} enabled={config.channels.whatsapp.enabled} onToggle={(v) => updateConfig('channels.whatsapp.enabled', v)} activeColor="emerald">
                    <TagInput
                      tags={config.channels.whatsapp.recipients}
                      icon={Phone}
                      onAdd={(tag) => updateRecipients('whatsapp', 'add', tag)}
                      onRemove={(i) => updateRecipients('whatsapp', 'remove', i)}
                      placeholder="Add phone number, press Enter"
                    />
                  </ChannelConfigRow>
                  <ChannelConfigRow label="Email" icon={Mail} enabled={config.channels.email.enabled} onToggle={(v) => updateConfig('channels.email.enabled', v)} activeColor="blue">
                    <TagInput
                      tags={config.channels.email.recipients}
                      icon={Mail}
                      onAdd={(tag) => updateRecipients('email', 'add', tag)}
                      onRemove={(i) => updateRecipients('email', 'remove', i)}
                      placeholder="Add email, press Enter"
                    />
                  </ChannelConfigRow>
                  <ChannelConfigRow label="SMS" icon={MessageSquare} enabled={config.channels.sms.enabled} onToggle={(v) => updateConfig('channels.sms.enabled', v)} activeColor="amber">
                    <TagInput
                      tags={config.channels.sms.recipients}
                      icon={Phone}
                      onAdd={(tag) => updateRecipients('sms', 'add', tag)}
                      onRemove={(i) => updateRecipients('sms', 'remove', i)}
                      placeholder="Add phone number, press Enter"
                    />
                  </ChannelConfigRow>
                </div>
              </ConfigSection>

              <AdvancedSection config={config} updateConfig={updateConfig} labelCls={labelCls} inputCls={inputCls} selectCls={selectCls} />
            </div>
          ) : (
            /* ══ VIEW MODE ══ */
            <div className="space-y-5">
              {/* Not configured banner */}
              {!config._backendId && (
                <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl px-6 py-8 text-center">
                  <Settings2 size={24} className="text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-slate-600">Not configured yet</p>
                  <p className="text-xs text-slate-400 mt-1 mb-4">This notification type hasn't been set up. Click Edit to configure and save.</p>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#232F3E] text-white text-xs font-semibold rounded-lg hover:bg-[#37475A] transition-colors"
                  >
                    <Pencil size={12} /> Configure Now
                  </button>
                </div>
              )}
              {config._backendId && (
              <>
              {/* Schedule */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-3.5 border-b border-slate-100">
                  <div className="w-7 h-7 rounded-lg bg-[#FFF4E6] border border-[#FFE8C8] flex items-center justify-center"><Calendar size={13} className="text-[#C45500]" /></div>
                  <h3 className="text-[13px] font-bold text-slate-800">Schedule</h3>
                </div>
                <div className="px-6 py-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <ViewPill label="Frequency" value={frequencyLabel[config.schedule.frequency]} color="blue" />
                    {isHourly && <ViewPill label="Data Range" value={hourlyRangeLabel[config.schedule.frequency]} color="indigo" />}
                    {!isHourly && <ViewPill label="Time" value={formatTime12(config.schedule.hour, config.schedule.minute, config.schedule.period)} color="indigo" />}
                    {(config.schedule.frequency === 'weekly' || config.schedule.frequency === 'biweekly') && (
                      <ViewPill label="Day" value={config.schedule.dayOfWeek} color="blue" />
                    )}
                    {config.schedule.frequency === 'monthly' && (
                      <ViewPill label="Day" value={`${config.schedule.dayOfMonth}${config.schedule.dayOfMonth === 1 ? 'st' : config.schedule.dayOfMonth === 2 ? 'nd' : config.schedule.dayOfMonth === 3 ? 'rd' : 'th'} of month`} color="blue" />
                    )}
                  </div>
                </div>
              </div>

              {/* Data Configuration */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-3.5 border-b border-slate-100">
                  <div className="w-7 h-7 rounded-lg bg-violet-50 border border-violet-100 flex items-center justify-center"><BarChart3 size={13} className="text-violet-600" /></div>
                  <h3 className="text-[13px] font-bold text-slate-800">Data Configuration</h3>
                </div>
                <div className="px-6 py-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <ViewPill label="Range" value={isHourly ? hourlyRangeLabel[config.schedule.frequency] : (dataRangeLabel[config.data.range] || `Last ${config.data.range}d`)} color="violet" />
                    <ViewPill label="Grouping" value={config.data.grouping === 'combined' ? 'Combined Report' : 'Per Channel'} color="violet" />
                    {!isHourly && <ViewPill label="Empty Reports" value={config.data.includeEmpty ? 'Included' : 'Excluded'} color={config.data.includeEmpty ? 'emerald' : 'slate'} />}
                  </div>
                </div>
              </div>

              {/* Delivery Channels */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-3.5 border-b border-slate-100">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center"><Send size={13} className="text-emerald-600" /></div>
                  <h3 className="text-[13px] font-bold text-slate-800">Delivery Channels</h3>
                </div>
                <div className="p-5 grid grid-cols-3 gap-4">
                  <ViewChannelTile label="WhatsApp" icon={Smartphone} enabled={config.channels.whatsapp.enabled} recipients={config.channels.whatsapp.recipients} color="emerald" />
                  <ViewChannelTile label="Email" icon={Mail} enabled={config.channels.email.enabled} recipients={config.channels.email.recipients} color="blue" />
                  <ViewChannelTile label="SMS" icon={MessageSquare} enabled={config.channels.sms.enabled} recipients={config.channels.sms.recipients} color="amber" />
                </div>
              </div>

              {/* Advanced */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <ViewAdvancedCollapsible config={config} priorityLabel={priorityLabel} thresholdLabel={thresholdLabel} />
              </div>
              </>
              )}
            </div>
          )}
        </div>

        {/* Action Bar — only show in edit mode, always outside disabled overlay */}
        {isEditing && (
          <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl px-6 py-4 flex items-center justify-between shadow-lg shadow-slate-200/50">
            <div className="flex items-center gap-3">
              <button onClick={() => setIsEditing(false)} className="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200">
                Cancel
              </button>
              <button onClick={handleReset} className="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors border border-transparent hover:border-slate-200">
                <RotateCw size={13} /> Reset to Defaults
              </button>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={handleTest} disabled={!config.isEnabled} className="inline-flex items-center gap-1.5 px-5 py-2.5 text-xs font-semibold text-[#C45500] bg-[#FFF4E6] hover:bg-[#FFE8C8] rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed border border-[#FFE8C8]">
                <Play size={13} /> Run Now
              </button>
              <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 px-6 py-2.5 text-xs font-semibold text-white bg-[#232F3E] hover:bg-[#37475A] rounded-lg transition-colors disabled:opacity-50 shadow-sm shadow-[#FFD89E]">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── View Mode Tile Components ──────────────────────────────────────────────

const pillColors = {
  blue: { valueBg: 'bg-[#FFF4E6]', valueText: 'text-[#C45500]', valueBorder: 'border-[#FFD89E]' },
  indigo: { valueBg: 'bg-[#FFF4E6]', valueText: 'text-[#C45500]', valueBorder: 'border-[#FFD89E]' },
  violet: { valueBg: 'bg-violet-50', valueText: 'text-violet-700', valueBorder: 'border-violet-200' },
  emerald: { valueBg: 'bg-emerald-50', valueText: 'text-emerald-700', valueBorder: 'border-emerald-200' },
  amber: { valueBg: 'bg-amber-50', valueText: 'text-amber-700', valueBorder: 'border-amber-200' },
  rose: { valueBg: 'bg-rose-50', valueText: 'text-rose-700', valueBorder: 'border-rose-200' },
  slate: { valueBg: 'bg-slate-50', valueText: 'text-slate-500', valueBorder: 'border-slate-200' },
};

function ViewPill({ label, value, color = 'indigo' }) {
  const c = pillColors[color] || pillColors.indigo;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] font-medium text-slate-400">{label}</span>
      <span className={`text-[11px] font-bold px-3 py-1 rounded-full border ${c.valueBg} ${c.valueText} ${c.valueBorder}`}>{value}</span>
    </div>
  );
}

function ViewChannelTile({ label, icon: Icon, enabled, recipients, color }) {
  const c = enabled ? (pillColors[color] || pillColors.indigo) : pillColors.slate;
  return (
    <div className={`rounded-xl border ${c.valueBorder} ${c.valueBg} px-4 py-3`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={13} className={enabled ? c.valueText : 'text-slate-400'} />
          <span className="text-[11px] font-bold text-slate-700">{label}</span>
        </div>
        <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${enabled ? `${c.valueBg} ${c.valueText} ${c.valueBorder}` : 'bg-slate-100 text-slate-400 border-slate-200'}`}>
          {enabled ? 'On' : 'Off'}
        </span>
      </div>
      {enabled && recipients.length > 0 ? (
        <div className="space-y-1">
          {recipients.map((r, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[11px] text-slate-600">
              <div className={`w-1 h-1 rounded-full ${c.valueText.replace('text-', 'bg-')}`} />
              <span className="truncate">{r}</span>
            </div>
          ))}
        </div>
      ) : enabled ? (
        <p className="text-[10px] text-slate-400 italic">No recipients</p>
      ) : (
        <p className="text-[10px] text-slate-300 italic">Not configured</p>
      )}
    </div>
  );
}

function ViewAdvancedCollapsible({ config, priorityLabel, thresholdLabel }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="flex items-center justify-between px-6 py-3.5 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center"><Settings2 size={13} className="text-slate-500" /></div>
          <h3 className="text-[13px] font-bold text-slate-800">Advanced Settings</h3>
        </div>
        <ChevronDown size={15} className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && (
        <div className="px-6 pb-4 border-t border-slate-100 pt-4">
          <div className="flex items-center gap-3 flex-wrap">
            <ViewPill label="Retries" value={`${config.advanced.retryAttempts}`} color="slate" />
            <ViewPill label="Priority" value={priorityLabel[config.advanced.priority]} color={config.advanced.priority === 'critical' ? 'rose' : config.advanced.priority === 'high' ? 'amber' : 'slate'} />
            <ViewPill label="Quiet Hours" value={config.advanced.quietHoursEnabled ? `${config.advanced.quietHoursStart} – ${config.advanced.quietHoursEnd}` : 'Off'} color={config.advanced.quietHoursEnabled ? 'indigo' : 'slate'} />
            <ViewPill label="Threshold" value={config.advanced.thresholdEnabled ? (thresholdLabel[config.advanced.thresholdCondition] || config.advanced.thresholdCondition) : 'Off'} color={config.advanced.thresholdEnabled ? 'amber' : 'slate'} />
          </div>
        </div>
      )}
    </>
  );
}

function ConfigSection({ title, icon: Icon, iconColor, iconBg, iconBorder, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
        <div className={`w-8 h-8 rounded-lg ${iconBg} border ${iconBorder} flex items-center justify-center`}><Icon size={14} className={iconColor} /></div>
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

function ChannelConfigRow({ label, icon: Icon, enabled, onToggle, activeColor, children }) {
  const borderColor = enabled ? `border-${activeColor}-200` : 'border-slate-200';
  const bgColor = enabled ? `bg-${activeColor}-50/30` : 'bg-slate-50/30';
  return (
    <div className={`rounded-xl border ${borderColor} ${bgColor} overflow-hidden transition-all`}>
      <div className="flex items-center justify-between px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <Icon size={15} className={enabled ? `text-${activeColor}-600` : 'text-slate-400'} />
          <span className="text-sm font-semibold text-slate-700">{label}</span>
        </div>
        <Toggle checked={enabled} onChange={onToggle} size="sm" />
      </div>
      {enabled && <div className="px-5 pb-4">{children}</div>}
    </div>
  );
}

function AdvancedSection({ config, updateConfig, labelCls, inputCls, selectCls }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center"><Settings2 size={14} className="text-slate-500" /></div>
          <h3 className="text-sm font-bold text-slate-800">Advanced Settings</h3>
        </div>
        <ChevronDown size={16} className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && (
        <div className="px-6 py-5 border-t border-slate-100 space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <div><label className={labelCls}>Retry Attempts</label><input type="number" min={1} max={5} value={config.advanced.retryAttempts} onChange={(e) => updateConfig('advanced.retryAttempts', Number(e.target.value))} className={inputCls} /></div>
            <div><label className={labelCls}>Priority</label><select value={config.advanced.priority} onChange={(e) => updateConfig('advanced.priority', e.target.value)} className={selectCls}><option value="normal">Normal</option><option value="high">High</option><option value="critical">Critical</option></select></div>
            <div className="col-span-2">
              <div className="bg-slate-50 rounded-lg border border-slate-100 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2"><VolumeX size={13} className="text-slate-400" /><span className="text-xs font-semibold text-slate-700">Quiet Hours</span></div>
                  <Toggle checked={config.advanced.quietHoursEnabled} onChange={(v) => updateConfig('advanced.quietHoursEnabled', v)} size="sm" />
                </div>
                {config.advanced.quietHoursEnabled && (
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div><label className="block text-[10px] font-medium text-slate-400 mb-1">Start</label><input type="time" value={config.advanced.quietHoursStart} onChange={(e) => updateConfig('advanced.quietHoursStart', e.target.value)} className={inputCls} /></div>
                    <div><label className="block text-[10px] font-medium text-slate-400 mb-1">End</label><input type="time" value={config.advanced.quietHoursEnd} onChange={(e) => updateConfig('advanced.quietHoursEnd', e.target.value)} className={inputCls} /></div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg border border-slate-100 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Zap size={13} className="text-slate-400" /><span className="text-xs font-semibold text-slate-700">Threshold Condition</span></div>
              <div className="flex items-center gap-3">
                {config.advanced.thresholdEnabled && (
                  <select value={config.advanced.thresholdCondition} onChange={(e) => updateConfig('advanced.thresholdCondition', e.target.value)} className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:border-transparent transition-colors">
                    <option value="orders_gt_0">Only send if orders &gt; 0</option>
                    <option value="sales_drop_10">Alert if sales drop &gt; 10%</option>
                    <option value="sales_drop_25">Alert if sales drop &gt; 25%</option>
                    <option value="returns_gt_5">Alert if returns &gt; 5</option>
                  </select>
                )}
                <Toggle checked={config.advanced.thresholdEnabled} onChange={(v) => updateConfig('advanced.thresholdEnabled', v)} size="sm" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════

export default function SellerDetailPage({ fixedTab, sellerId: propSellerId }) {
  const params = useParams();
  const id = propSellerId || params.id;
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(fixedTab || 'overview');

  const [editModal, setEditModal] = useState(false);
  const [platformPicker, setPlatformPicker] = useState(false);
  const [amazonModal, setAmazonModal] = useState(false);
  const [flipkartModal, setFlipkartModal] = useState(false);
  const [meeshoModal, setMeeshoModal] = useState(false);
  const [reconnectModal, setReconnectModal] = useState({ open: false, account: null });
  const [marketplaceModal, setMarketplaceModal] = useState({ open: false, account: null });
  const [adsModal, setAdsModal] = useState({ open: false, accountId: null });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', variant: 'warning', action: null });
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [healthCheckingId, setHealthCheckingId] = useState(null);

  const loadSeller = useCallback(async () => {
    try {
      const res = await sellerService.getById(id);
      setSeller(res.data || res);
    } catch {
      toast.error('Failed to load seller');
      navigate('/seller-accounts');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadSeller(); }, [loadSeller]);

  const handleEditSave = async (formData) => { try { await sellerService.update(id, formData); toast.success('Seller updated'); setEditModal(false); loadSeller(); } catch (err) { toast.error(err?.response?.data?.message || 'Failed to update'); } };
  const handlePlatformSelect = (platform) => { setPlatformPicker(false); if (platform === 'AMAZON') setAmazonModal(true); else if (platform === 'FLIPKART') setFlipkartModal(true); else if (platform === 'MEESHO') setMeeshoModal(true); };
  const handleConnect = async (data) => { try { await sellerAccountService.connect(data); toast.success('Channel connected'); setAmazonModal(false); setFlipkartModal(false); setMeeshoModal(false); loadSeller(); } catch (err) { toast.error(err?.response?.data?.message || 'Connection failed'); } };
  const handleHealthCheck = async (channel) => { setHealthCheckingId(channel.id); try { const res = await sellerAccountService.checkHealth(channel.id); const d = res.data || res; d.status === 'HEALTHY' ? toast.success(`${channel.platform} is healthy`) : toast.error(d.message || 'Check failed'); } catch { toast.error('Health check failed'); } finally { setHealthCheckingId(null); } };
  const handleDisconnect = (channel) => { setConfirmModal({ open: true, variant: 'warning', title: 'Disconnect Channel', message: `Disconnect "${channel.sellerName || channel.platform}"?`, action: async () => { await sellerAccountService.disconnect(channel.id); toast.success('Disconnected'); loadSeller(); } }); };
  const handleDeleteChannel = (channel) => { setConfirmModal({ open: true, variant: 'danger', title: 'Delete Channel', message: `Permanently delete this ${channel.platform} connection?`, action: async () => { await sellerAccountService.delete(channel.id); toast.success('Deleted'); loadSeller(); } }); };
  const handleReconnect = async (data) => { try { await sellerAccountService.reconnect(reconnectModal.account.id, data); toast.success('Reconnected'); setReconnectModal({ open: false, account: null }); loadSeller(); } catch { toast.error('Failed'); } };
  const handleConfirm = async () => { if (!confirmModal.action) return; setConfirmLoading(true); try { await confirmModal.action(); } catch (err) { toast.error(err?.response?.data?.message || 'Action failed'); } finally { setConfirmLoading(false); setConfirmModal({ open: false, title: '', message: '', variant: 'warning', action: null }); } };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-32">
          <div className="text-center"><Loader2 size={32} className="mx-auto text-[#f08804] animate-spin mb-3" /><p className="text-sm text-slate-500">Loading seller...</p></div>
        </div>
      </AdminLayout>
    );
  }

  if (!seller) return null;

  const ss = sellerStatusConfig[seller.status] || sellerStatusConfig.ACTIVE;
  const channels = seller.channels || [];
  const connectedCount = channels.filter(c => c.accountStatus === 'AUTHORIZED').length;
  const uniquePlatforms = [...new Set(channels.map(c => c.platform))];

  return (
    <AdminLayout>
      <div className="h-full flex flex-col">
        {/* Top Header */}
        {fixedTab ? (
          /* Compact header for standalone top-nav pages */
          <div className="bg-white border-b border-[#D5D9D9] px-4 sm:px-6 py-2 flex items-center justify-between flex-shrink-0">
            <h1 className="text-[15px] font-bold text-[#0F1111]">
              {fixedTab === 'channels' && 'Marketplaces'}
              {fixedTab === 'overview' && 'Seller Information'}
              {fixedTab === 'notifications' && 'Notification Config'}
            </h1>
          </div>
        ) : (
          /* Full header with tabs for direct access via /sellers/:id */
          <div className="bg-gradient-to-r from-slate-50 via-white to-[#FFF4E6]/30 border-b border-slate-200 px-8 pt-5 pb-0 flex-shrink-0">
            {/* Back + Breadcrumb */}
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-[#C45500] transition-colors mb-4 group">
              <ArrowLeft size={13} className="group-hover:-translate-x-0.5 transition-transform" />
              <span>Back</span>
              <span className="text-slate-300 mx-1">/</span>
              <span className="text-slate-600">{seller.businessName}</span>
            </button>

            {/* Seller Identity Bar */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#f08804] to-[#37475A] flex items-center justify-center shadow-lg shadow-[#FFD89E]/50 flex-shrink-0">
                  <Building2 size={24} className="text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{seller.businessName}</h1>
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${ss.bg} ${ss.color} ${ss.border} uppercase tracking-wider`}>{ss.label}</span>
                  </div>
                  <div className="flex items-center gap-5">
                    {seller.gstin && <span className="text-xs text-slate-400 flex items-center gap-1.5 font-mono"><Hash size={10} className="text-slate-300" />{seller.gstin}</span>}
                    {seller.contactEmail && <span className="text-xs text-slate-400 flex items-center gap-1.5"><Mail size={10} className="text-slate-300" />{seller.contactEmail}</span>}
                    {seller.contactPhone && <span className="text-xs text-slate-400 flex items-center gap-1.5"><Phone size={10} className="text-slate-300" />{seller.contactPhone}</span>}
                    <span className="text-xs text-slate-400 flex items-center gap-1.5"><Plug size={10} className="text-slate-300" />{connectedCount}/{channels.length} channels</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {uniquePlatforms.length > 0 && (
                  <div className="flex -space-x-2 mr-1">
                    {uniquePlatforms.map(p => {
                      const pc = platformConfig[p];
                      return pc ? <div key={p} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${pc.gradient} flex items-center justify-center border-2 border-white shadow-sm`}><span className={`text-xs font-bold ${pc.text}`}>{pc.letter}</span></div> : null;
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-0 -mb-px">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isTabActive = activeTab === tab.id;
                let badge = null;
                if (tab.id === 'channels' && channels.length > 0) badge = channels.length;
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-all ${isTabActive ? 'text-[#C45500] border-[#C45500] bg-white/50' : 'text-slate-400 border-transparent hover:text-slate-600 hover:border-slate-300'}`}>
                    <Icon size={15} />
                    {tab.label}
                    {badge != null && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${isTabActive ? 'bg-[#FFE8C8] text-[#C45500]' : 'bg-slate-100 text-slate-500'}`}>{badge}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab Content - Scrollable, Full Width */}
        <div className="flex-1 overflow-auto">
          <div className="px-3 sm:px-6 py-4">
            {activeTab === 'overview' && <OverviewTab seller={seller} onEdit={() => setEditModal(true)} />}
            {activeTab === 'channels' && (
              <ChannelsTab seller={seller}
                onConnectChannel={() => setPlatformPicker(true)}
                onHealthCheck={handleHealthCheck}
                onMarketplace={(ch) => setMarketplaceModal({ open: true, account: ch })}
                onConnectAds={(ch) => setAdsModal({ open: true, accountId: ch.id })}
                onReconnect={(ch) => setReconnectModal({ open: true, account: ch })}
                onDisconnect={handleDisconnect}
                onDelete={handleDeleteChannel}
                healthCheckingId={healthCheckingId}
              />
            )}
            {activeTab === 'notifications' && <NotificationsTab seller={seller} />}
          </div>
        </div>
      </div>

      {/* Modals */}
      <EditSellerModal isOpen={editModal} onClose={() => setEditModal(false)} onSave={handleEditSave} editData={seller} />
      <ConnectPlatformPicker isOpen={platformPicker} onClose={() => setPlatformPicker(false)} onSelect={handlePlatformSelect} />
      <AmazonConnectModal isOpen={amazonModal} onClose={() => setAmazonModal(false)} onConnect={handleConnect} sellerId={id} />
      <FlipkartConnectModal isOpen={flipkartModal} onClose={() => setFlipkartModal(false)} onConnect={handleConnect} sellerId={id} />
      <MeeshoConnectModal isOpen={meeshoModal} onClose={() => setMeeshoModal(false)} onConnect={handleConnect} sellerId={id} />
      <ReconnectModal isOpen={reconnectModal.open} onClose={() => setReconnectModal({ open: false, account: null })} onReconnect={handleReconnect} account={reconnectModal.account} />
      {marketplaceModal.account && <MarketplaceModal isOpen={marketplaceModal.open} onClose={() => setMarketplaceModal({ open: false, account: null })} accountId={marketplaceModal.account?.id} platform={marketplaceModal.account?.platform} sellerName={marketplaceModal.account?.sellerName} />}
      <AdsConnectModal isOpen={adsModal.open} onClose={() => setAdsModal({ open: false, accountId: null })} onConnect={() => loadSeller()} sellerAccountId={adsModal.accountId} />
      <ConfirmModal isOpen={confirmModal.open} onClose={() => setConfirmModal({ open: false, title: '', message: '', variant: 'warning', action: null })} onConfirm={handleConfirm} title={confirmModal.title} message={confirmModal.message} variant={confirmModal.variant} loading={confirmLoading} />
    </AdminLayout>
  );
}